import React from "react"
import { Link, useLocation } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Phone,
  Mail,
  Clock,
  Globe,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  FileText,
  Calendar,
  MessageSquare,
} from "lucide-react"
import { PatientResponse, PatientAPCMConsentResponse } from "@/lib/types"
import { formatDistanceToNow } from "date-fns"
import { formatDateOnly, formatTimestamp, parseDateOnly } from "@/lib/datetime"

interface CareGap {
  id: string
  name: string
  dueDate: string | null
  status: "overdue" | "due_soon" | "upcoming"
}

interface ActionItem {
  id: string
  title: string
  assignee?: string
  dueDate?: string
  source?: string
}

interface ActivityItem {
  id: string
  type: "call" | "sms" | "note" | "care_gap" | "consent" | "action_item"
  title: string
  description?: string
  date: string
  callId?: string
}

interface PatientOverviewProps {
  patient: PatientResponse
  consent: PatientAPCMConsentResponse | null
  careGaps?: CareGap[]
  actionItems?: ActionItem[]
  recentActivity?: ActivityItem[]
  loading?: boolean
  onUpdateConsent?: () => void
  onEditContact?: () => void
}

export function PatientOverview({
  patient,
  consent,
  careGaps = [],
  actionItems = [],
  recentActivity = [],
  loading = false,
  onUpdateConsent,
  onEditContact,
}: PatientOverviewProps) {
  const location = useLocation()
  const returnUrl = `${location.pathname}${location.search}`

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-64 md:col-span-2" />
      </div>
    )
  }

  const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return null
    const cleaned = phone.replace(/\D/g, "")
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
    }
    return phone
  }

  const getActivityIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "call":
        return <Phone className="h-4 w-4 text-info" />
      case "sms":
        return <MessageSquare className="h-4 w-4 text-success" />
      case "note":
        return <FileText className="h-4 w-4 text-muted-foreground" />
      case "care_gap":
        return <CheckCircle className="h-4 w-4 text-success" />
      case "consent":
        return <FileText className="h-4 w-4 text-primary" />
      case "action_item":
        return <Calendar className="h-4 w-4 text-warning" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        {/* APCM Status Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              APCM Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {consent ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {consent.consent_status === "active"
                      ? "Consented"
                      : `Consent ${consent.consent_status}`}
                  </span>
                  {consent.created_at && (
                    <span className="text-sm text-muted-foreground">
                      ({formatTimestamp(consent.created_at, "MMM yyyy")})
                    </span>
                  )}
                </div>
                {consent.method_of_consent && (
                  <div className="text-sm text-muted-foreground">
                    Method: {consent.method_of_consent}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onUpdateConsent}
                  className="mt-2"
                >
                  Update Status
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-destructive">
                  <span className="font-medium">No Consent</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Patient has not provided APCM consent
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onUpdateConsent}
                  className="mt-2"
                >
                  Add Consent
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Contact Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {patient.phone_number && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{formatPhone(patient.phone_number)}</span>
                  <Badge variant="outline" className="text-xs">
                    Mobile
                  </Badge>
                </div>
              )}
              {patient.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{patient.email}</span>
                </div>
              )}
              {(patient.notes as any)?.preferences?.time_to_call && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>Best time: {(patient.notes as any).preferences.time_to_call}</span>
                </div>
              )}
              {(patient.notes as any)?.preferences?.language && (
                <div className="flex items-center gap-2 text-sm">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <span>{(patient.notes as any).preferences.language}</span>
                </div>
              )}
              {!patient.phone_number && !patient.email && (
                <p className="text-sm text-muted-foreground italic">
                  No contact information available
                </p>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onEditContact}
                className="mt-2 -ml-2"
              >
                Edit Contact Info
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Open Care Gaps Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open Care Gaps ({careGaps.length})
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="care-gaps">
                View all <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {careGaps.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-success" />
                <p>No open care gaps</p>
              </div>
            ) : (
              <div className="space-y-2">
                {careGaps.slice(0, 3).map((gap) => (
                  <div
                    key={gap.id}
                    className="flex items-center justify-between py-1"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          gap.status === "overdue"
                            ? "bg-destructive"
                            : gap.status === "due_soon"
                            ? "bg-warning"
                            : "bg-muted-foreground/40"
                        }`}
                      />
                      <span className="text-sm">{gap.name}</span>
                    </div>
                    {gap.dueDate && (
                      <span
                        className={`text-xs ${
                          gap.status === "overdue"
                            ? "text-destructive"
                            : "text-muted-foreground"
                        }`}
                      >
                        {gap.status === "overdue"
                          ? `${formatDistanceToNow(parseDateOnly(gap.dueDate) ?? new Date())} overdue`
                          : formatDateOnly(gap.dueDate, "MMM d")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Items Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Action Items ({actionItems.length})
            </CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="action-items">
                View all <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {actionItems.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-success" />
                <p>No open action items</p>
              </div>
            ) : (
              <div className="space-y-2">
                {actionItems.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-2 py-1"
                  >
                    <div className="w-4 h-4 border rounded mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{item.title}</p>
                      {item.assignee && (
                        <p className="text-xs text-muted-foreground">
                          Assigned to: {item.assignee}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity - Full Width */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Recent Activity
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="calls">
              View all <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentActivity.slice(0, 5).map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 py-2 border-b last:border-0"
                >
                  <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">
                        {activity.title}
                      </p>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatTimestamp(activity.date, "MMM d")}
                      </span>
                    </div>
                    {activity.description && (
                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                        {activity.description}
                      </p>
                    )}
                  </div>
                  {activity.callId && (
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/calls/${activity.callId}`} state={{ returnUrl }}>View</Link>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default PatientOverview
